 
/**
 * Organization Service
 *
 * API client for organization management.
 */

import { buildQueryString, type ApiResponse, type PageInfo } from './api-client';
import { buildPaginationParams } from '../utils/api-helpers';

import type { AuditInfo } from '../types/common';

// Re-export AuditInfo for backward compatibility
export type { AuditInfo } from '../types/common';

// ============================================================================
// Accounts API Client (via Accounting Server Proxy)
// ============================================================================

interface ErrorResponseBody {
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
}

// Use accounting server which proxies to accounts-api
// This avoids CORS issues
const ACCOUNTS_API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * Make a request to the accounts-api service
 */
async function accountsApiRequest<T>(
  path: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const url = `${ACCOUNTS_API_BASE_URL}${path}`;
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as ErrorResponseBody;
      return {
        error: {
          message: errorData.message ?? `Request failed: ${response.statusText}`,
          code: errorData.code ?? response.status.toString(),
          details: errorData.details,
        },
      };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {
        data: undefined as unknown as T,
      };
    }

    const data = await response.json() as T;
    return {
      data,
    };
  } catch (error) {
    return {
      error: {
        message: error instanceof Error ? error.message : 'Network request failed',
        code: 'NETWORK_ERROR',
      },
    };
  }
}

// ============================================================================
// Types
// ============================================================================

export interface Organization {
  id: string;
  code: string;
  name: string;
  entityId?: string;
  fiscalYearStartMonth: number;
  defaultCurrency: string;
  defaultLedgerId: string; // ID of the MAIN ledger for this organization
  timezone: string;
  status: 'active' | 'suspended' | 'closed';
  locale: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  logoUrl?: string; // URL to organization logo
  enabledCurrencies: string[]; // ISO 4217 currency codes enabled for this org
  documentDisplayConfig?: import('../stores/settings-store').DocumentDisplayConfig;
  defaultCreditDays?: number; // 0, 7, 15, 30, 45, 60
  /** GST ledger posting granularity: 'consolidated' (default) | 'rate_wise'. */
  gstLedgerGranularity?: string;
  /** Default party role pre-filled on sales-direction documents. '' = built-in "customer". */
  defaultSalesRole?: string;
  /** Default party role pre-filled on purchase-direction documents. '' = built-in "supplier". */
  defaultPurchaseRole?: string;
  audit: AuditInfo;
}

export interface CreateOrganizationRequest {
  code: string;
  name: string;
  entityId?: string;
  fiscalYearStartMonth: number;
  defaultCurrency: string;
  timezone: string;
  locale?: string;
  dateFormat?: string;
  timeFormat?: '12h' | '24h';
}

export interface UpdateOrganizationRequest {
  name?: string;
  entityId?: string;
  fiscalYearStartMonth?: number;
  defaultCurrency?: string;
  timezone?: string;
  locale?: string;
  dateFormat?: string;
  timeFormat?: '12h' | '24h';
  enabledCurrencies?: string[];
  logoUrl?: string;
  documentDisplayConfig?: import('../stores/settings-store').DocumentDisplayConfig;
  defaultCreditDays?: number; // 0, 7, 15, 30, 45, 60
  /** GST ledger posting granularity: 'consolidated' | 'rate_wise'. */
  gstLedgerGranularity?: string;
  /** Default party role for sales-direction documents. */
  defaultSalesRole?: string;
  /** Default party role for purchase-direction documents. */
  defaultPurchaseRole?: string;
}

export interface ListOrganizationsResponse {
  organizations: Organization[];
  pageInfo: PageInfo;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get organization by ID.
 */
export async function getOrganization(orgId: string): Promise<ApiResponse<Organization>> {
  return accountsApiRequest<Organization>(`/organizations/${orgId}`);
}

/**
 * Update organization settings.
 */
export async function updateOrganization(
  orgId: string,
  data: UpdateOrganizationRequest
): Promise<ApiResponse<Organization>> {
  return accountsApiRequest<Organization>(`/organizations/${orgId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * List organizations for current user.
 */
export async function listOrganizations(
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<ListOrganizationsResponse>> {
  const query = buildQueryString(buildPaginationParams(pageSize, pageToken));
  return accountsApiRequest<ListOrganizationsResponse>(`/organizations${query}`);
}

/**
 * Create a new organization.
 */
export async function createOrganization(
  data: CreateOrganizationRequest
): Promise<ApiResponse<Organization>> {
  return accountsApiRequest<Organization>('/organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Delete an organization and all its data.
 * WARNING: This is irreversible and will delete all ledgers, accounts, entries, etc.
 */
export async function deleteOrganization(orgId: string): Promise<ApiResponse<void>> {
  return accountsApiRequest<void>(`/organizations/${orgId}`, {
    method: 'DELETE',
  });
}
